import React from 'react';

import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { tenantProfileLabelMap } from '@types';
import type { TenantProfileLabelKey, ToneType } from '@types';
import { formatNumber } from '@utils/parsing';

interface RatioBarProps {
    ratio: {
        label?: string;
        field: TenantProfileLabelKey;
        value: number;
        compareTo?: number;
        hint?: string;
        tone?: ToneType;
    };
}

const PERCENT_FIELDS: TenantProfileLabelKey[] = ['profitMargin', 'equityRatio', 'cashRatio', 'netResult', 'profitAfterFinancialItems'];

// eslint-disable-next-line
const toneColor = (tone?: ToneType) => tone === 'good' ? 'success' : tone === 'bad' ? 'error' : 'primary';

const clampProgress = (value: number): number => Math.max(0, Math.min(value, 100));

const formatMetricValue = (field: TenantProfileLabelKey, value: number): string | number => {
    if (PERCENT_FIELDS.includes(field)) {
        return formatNumber(Math.round(value), '%');
    }

    return formatNumber(value);
};

const resolveProgressValue = (
    { field, value, compareTo }
        : { field: TenantProfileLabelKey; value: number; compareTo?: number }
): number => {
    const magnitude = Math.abs(value);

    if (typeof compareTo === 'number' && compareTo > 0) {
        return clampProgress((magnitude / compareTo) * 100);
    }

    if (PERCENT_FIELDS.includes(field)) {
        return clampProgress(magnitude);
    }

    return clampProgress(magnitude);
};

const resolveTone = ({
    field,
    value
}: {
    field?: TenantProfileLabelKey;
    value: unknown;
}): ToneType => {
    if (!field) return 'neutral';

    if (field === 'profitMargin' && typeof value === 'number') {
        if (value < 5) {
            return 'bad';
        }
        if (value <= 10) {
            return 'neutral';
        }
        return 'good';
    }

    if (field === 'equityRatio' && typeof value === 'number') {
        if (value < 20) {
            return 'bad';
        }
        if (value <= 30) {
            return 'neutral';
        }
        return 'good';
    }

    if (field === 'cashRatio' && typeof value === 'number') {
        if (value < 1) {
            return 'bad';
        }
        if (value <= 1.5) {
            return 'neutral';
        }
        return 'good';
    }

    if ((field === 'netResult' || field === 'profitAfterFinancialItems') && typeof value === 'number') {
        if (value < 0) {
            return 'bad';
        }
        if (value <= 5) {
            return 'neutral';
        }
        return 'good';
    }
    return 'neutral';
};

const RatioBar: React.FC<RatioBarProps> = ({ ratio }) => {
    const {
        label,
        field,
        value,
        compareTo,
        hint,
        tone
    } = ratio;

    const resolvedTone = tone ?? resolveTone({ field, value });
    const color = toneColor(resolvedTone);
    const resolvedLabel = label ?? (field ? tenantProfileLabelMap[field] : '-');

    return (
        <Box
            sx={{
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': { borderBottom: 0 }
            }}
        >
            <Box sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 2
            }}
            >
                <Typography variant="body2" color="text.secondary">
                    {resolvedLabel}
                </Typography>
                <Box
                    component="span"
                    sx={{
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 600,
                        fontSize: 14,
                        color
                    }}
                >
                    {formatMetricValue(field, value)}
                </Box>
            </Box>
            {hint && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {hint}
                </Typography>
            )}
            {typeof value === 'number' && (
                <LinearProgress
                    variant="determinate"
                    color={color}
                    value={resolveProgressValue({ field, value, compareTo })}
                    sx={{ mt: 1 }}
                />
            )}
        </Box>
    );
};

export default RatioBar;
